import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export const DEFAULT_AI_ENGINE_SETTINGS = {
  enabled: true,
  profile: "balanced", // economy | balanced | strict
  directivePrompt: "",
  deepthink: "balanced", // low | balanced | high
  contextMode: "balanced", // focused | balanced | broad | full
  qualityMode: "balanced", // fast | balanced | strict
  breadthMode: "standard", // narrow | standard | wide
  models: {
    precision: "claude-sonnet-4-6",
    pro: "claude-opus-4-7",
    fast: "claude-haiku-4-5-20251001",
  },
  maxTokens: {
    precision: 10240,
    trails: 10240,
    fast: 4096,
  },
  toggles: {
    retryJson: true,
    escalation: true,
    validation: true,
    schema: true,
  },
  moduleProfiles: {
    "tools/ai": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/ncs": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/photo": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/assistente": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/changes": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/trails": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "tools/meetings": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "audit/preparation": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "audit/checklist": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "audit/report": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "audit/program": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
    "audit/followup": { enabled: false, profile: "inherit", deepthink: "inherit", contextMode: "inherit", qualityMode: "inherit", breadthMode: "inherit", models: { precision: "", pro: "", fast: "" }, maxTokens: { precision: 0, trails: 0, fast: 0 } },
  },
};

function mergeModuleProfiles(inputProfiles = {}) {
  const merged = JSON.parse(JSON.stringify(DEFAULT_AI_ENGINE_SETTINGS.moduleProfiles));
  for (const key of Object.keys(inputProfiles || {})) {
    const incoming = inputProfiles[key] || {};
    if (!merged[key]) merged[key] = JSON.parse(JSON.stringify(DEFAULT_AI_ENGINE_SETTINGS.moduleProfiles["tools/ai"]));
    merged[key] = {
      ...merged[key],
      ...incoming,
      models: { ...merged[key].models, ...(incoming.models || {}) },
      maxTokens: { ...merged[key].maxTokens, ...(incoming.maxTokens || {}) },
    };
  }
  return merged;
}

export function normalizeAIEngineSettings(settings) {
  const s = settings || {};
  return {
    ...DEFAULT_AI_ENGINE_SETTINGS,
    ...s,
    models: { ...DEFAULT_AI_ENGINE_SETTINGS.models, ...(s.models || {}) },
    maxTokens: { ...DEFAULT_AI_ENGINE_SETTINGS.maxTokens, ...(s.maxTokens || {}) },
    toggles: { ...DEFAULT_AI_ENGINE_SETTINGS.toggles, ...(s.toggles || {}) },
    moduleProfiles: mergeModuleProfiles(s.moduleProfiles),
  };
}

export const useAIEngineStore = create(
  persist(
    (set) => ({
      settings: normalizeAIEngineSettings(DEFAULT_AI_ENGINE_SETTINGS),
      setSettings: (settings) => set({ settings: normalizeAIEngineSettings(settings) }),
      resetSettings: () => set({ settings: normalizeAIEngineSettings(DEFAULT_AI_ENGINE_SETTINGS) }),
    }),
    {
      name: "norm_ai_engine_store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ settings: state.settings }),
    },
  ),
);

